import React from 'react';
import { UIHelper, StatusHelper } from '../../utils/crudHelpers';

// Column definition interface
export interface TableColumn<T = any> {
  key: string;
  title: string;
  width?: string;
  sortable?: boolean; // Added for sorting
  render?: (value: any, item: T, index: number) => React.ReactNode;
  editable?: boolean;
  editType?: 'text' | 'number' | 'select' | 'email';
  selectOptions?: Array<{ value: string; label: string }>;
  onEdit?: (item: T, key: string, newValue: any) => Promise<void>;
}

// Action button interface
export interface TableAction<T = any> {
  label: string;
  onClick: (item: T) => Promise<void>;
  variant?: 'primary' | 'secondary' | 'danger' | 'success';
  icon?: string;
  condition?: (item: T) => boolean;
}

// Props interface
interface StandardTableProps<T = any> {
  data: T[];
  columns: TableColumn<T>[];
  actions?: TableAction<T>[];
  loading?: boolean;
  emptyMessage?: string;
  sortConfig?: { key: string; direction: string } | null;
  onSort?: (key: string) => void;
}

export function StandardTable<T extends { id: string }>({
  data,
  columns,
  actions = [],
  loading = false,
  emptyMessage = 'No hay datos disponibles',
  sortConfig,
  onSort,
}: StandardTableProps<T>) {
  const tableStyle = UIHelper.getStandardTableStyle();
  const headerStyle = UIHelper.getStandardTableCellStyle(true);
  const cellStyle = UIHelper.getStandardTableCellStyle(false);

  const handleEdit = async (item: T, column: TableColumn<T>, newValue: any) => {
    if (column.onEdit) {
      try {
        await column.onEdit(item, column.key, newValue);
      } catch (error) {
        console.error('Edit failed:', error);
        alert('Error al actualizar. Inténtalo de nuevo.');
      }
    }
  };

  const renderEditableCell = (item: T, column: TableColumn<T>, value: any) => {
    if (!column.editable) {
      return column.render ? column.render(value, item, 0) : value;
    }

    const inputStyle = UIHelper.getStandardInputStyle();

    switch (column.editType) {
      case 'select':
        return (
          <select
            value={value || ''}
            onChange={(e) => handleEdit(item, column, e.target.value)}
            style={{ ...inputStyle, width: '100%' }}
          >
            {column.selectOptions?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'number':
        return (
          <input
            type="number"
            value={value || 0}
            onChange={(e) => handleEdit(item, column, Number(e.target.value))}
            style={{ ...inputStyle, width: '100%' }}
          />
        );

      case 'email':
        return (
          <input
            type="email"
            value={value || ''}
            onChange={(e) => handleEdit(item, column, e.target.value)}
            style={{ ...inputStyle, width: '100%' }}
          />
        );

      default: // text
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleEdit(item, column, e.target.value)}
            style={{ ...inputStyle, width: '100%' }}
          />
        );
    }
  };

  const renderActions = (item: T) => {
    const visibleActions = actions.filter(action =>
      !action.condition || action.condition(item)
    );

    if (visibleActions.length === 0) return null;

    return (
      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
        {visibleActions.map((action, index) => (
          <button
            key={index}
            onClick={() => action.onClick(item)}
            style={{
              ...UIHelper.getStandardButtonStyle(action.variant),
              padding: '4px 8px',
              fontSize: '12px'
            }}
          >
            {action.icon && `${action.icon} `}{action.label}
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
        Cargando datos...
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
        {emptyMessage}
      </div>
    );
  }

  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          {columns.map(column => (
            <th
              key={column.key}
              style={{
                ...headerStyle,
                width: column.width,
                cursor: column.sortable ? 'pointer' : 'default',
                userSelect: 'none',
              }}
              onClick={() => column.sortable && onSort && onSort(column.key)}
            >
              {column.title}
              {sortConfig && sortConfig.key === column.key ? (
                sortConfig.direction === 'ascending' ? ' 🔼' : ' 🔽'
              ) : null}
            </th>
          ))}
          {actions.length > 0 && (
            <th style={headerStyle}>Acciones</th>
          )}
        </tr>
      </thead>
      <tbody>
        {data.map((item, index) => (
          <tr key={item.id}>
            {columns.map(column => (
              <td key={column.key} style={cellStyle}>
                {renderEditableCell(item, column, (item as any)[column.key])}
              </td>
            ))}
            {actions.length > 0 && (
              <td style={cellStyle}>
                {renderActions(item)}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Helper function to create standard status column
export const createStatusColumn = <T extends { active?: boolean; habilitada?: boolean; habilitado?: boolean }>(
  onToggle?: (item: T) => Promise<void>
): TableColumn<T> => ({
  key: 'status',
  title: 'Estado',
  width: '10%',
  render: (_, item) => {
    const isActive = item.active ?? item.habilitada ?? item.habilitado ?? false;
    return (
      <div style={{ textAlign: 'center' }}>
        {StatusHelper.getStatusIcon(isActive)}
      </div>
    );
  }
});

// Helper function to create standard actions
export const createStandardActions = <T extends { id: string; active?: boolean; habilitada?: boolean; habilitado?: boolean }>(
  onToggle?: (item: T) => Promise<void>,
  onDelete?: (item: T) => Promise<void>,
  customActions: TableAction<T>[] = []
): TableAction<T>[] => {
  const actions: TableAction<T>[] = [...customActions];

  if (onToggle) {
    actions.push({
      label: 'Estado',
      variant: 'secondary',
      onClick: onToggle,
      icon: '🔄',
      condition: (item) => {
        // Always show toggle button regardless of current state
        return true;
      }
    });
  }

  if (onDelete) {
    actions.push({
      label: 'Eliminar',
      variant: 'danger',
      onClick: onDelete
    });
  }

  return actions;
};